import React, {useMemo, useState} from 'react';
import {
    Box,
    Button,
    Card,
    Container,
    IconButton,
    InputAdornment,
    Link as MuiLink,
    TextField,
    Typography,
} from '@mui/material';
import {Link, useLocation} from 'react-router-dom';
import axios from 'axios';
import Logo from '../core/components/Logo';
import {ThemedText} from '../core/components/ThemedText';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const RESET_PASSWORD_ENDPOINT = '/auth/reset-password';

const useQuery = () => {
    const location = useLocation();
    return useMemo(() => new URLSearchParams(location.search), [location.search]);
};

const ResetPassword = () => {
    const query = useQuery();
    const initialToken = query.get('token') || '';
    const [token, setToken] = useState(initialToken);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const handleSubmit = async (event) => {
        event.preventDefault();
        setErrorMessage('');
        setSuccessMessage('');

        if (!token || !newPassword) {
            setErrorMessage('Please enter your reset token and new password.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setErrorMessage('Passwords do not match.');
            return;
        }

        setSubmitting(true);
        try {
            const client = axios.create({
                baseURL: process.env.REACT_APP_API_URL,
            });

            await client.post(RESET_PASSWORD_ENDPOINT, { token, newPassword });
            setSuccessMessage('Password updated successfully. You can now sign in.');
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            setErrorMessage('Unable to reset password. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Container maxWidth="xs">
            <Box
                sx={{
                    marginTop: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Card sx={{ p: 3, width: '100%' }}>
                    <Logo />
                    <ThemedText style={{ marginTop: '20px', marginBottom: '20px'}} align="center" type="title">
                        Set new password
                    </ThemedText>
                    <ThemedText style={{ marginBottom: '20px' }} align="center" type="subtitle">
                        Enter your reset token and choose a new password.
                    </ThemedText>
                    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
                        {errorMessage && (
                            <Typography variant="body2" color="error" align="center" sx={{ mb: 2 }}>
                                {errorMessage}
                            </Typography>
                        )}
                        {successMessage && (
                            <Typography variant="body2" color="success.main" align="center" sx={{ mb: 2 }}>
                                {successMessage}
                            </Typography>
                        )}
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="token"
                            label="Reset Token"
                            name="token"
                            value={token}
                            onChange={(e) => setToken(e.target.value)}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="newPassword"
                            label="New Password"
                            type={showNewPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                                            onClick={() => setShowNewPassword((prev) => !prev)}
                                            edge="end"
                                        >
                                            {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="confirmPassword"
                            label="Confirm New Password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                                            onClick={() => setShowConfirmPassword((prev) => !prev)}
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '15px'}}>
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={submitting}
                                sx={{ mt: 3, mb: 2 }}
                            >
                                {submitting ? 'Updating...' : 'Update password'}
                            </Button>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Link to="/login" style={{ textDecoration: 'none'}}>
                                <MuiLink variant="body2" style={{ textDecoration: 'none'}}>
                                    Back to sign in
                                </MuiLink>
                            </Link>
                        </Box>
                    </Box>
                </Card>
            </Box>
        </Container>
    );
};

export default ResetPassword;
